/* eslint-disable max-lines */
import { autorun } from 'mobx';
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { SortAndFiltersViewType } from '@shared-ui/search-tools-lodging';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { evaluateAndLogExperiment } from 'stores/utils';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';

import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkEGMapsRef } from 'uitk-react-map';
import { UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkTertiaryButton } from 'uitk-react-button';

import { Bounds, EGMap, FeatureObject, LatLngArray } from '@eg-maps/core';
import { FiltersToggle } from 'components/search/sort-filter/filters-toggle';
import { PropertyFields } from 'src/common/__generated__/api';

interface MapInfo {
  mapObj: any;
  mapRef: React.RefObject<any>;
  preloadedHotelLoaded: boolean;
}

export interface SearchResultsMapProps extends Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  page?: PageStore;
  currentHotel?: CurrentHotelStore;
  experiment?: ExperimentStore;
  onDismiss?: () => void;
  searchResults?: SearchResultsStore;
  searchCriteria?: SearchCriteriaModel;
  preloadedTravelAdEnabled?: boolean;
  dialogView?: SortAndFiltersViewType;
}

export interface SearchResultsMapState {
  center?: any;
  markerSelected?: boolean;
  selectedHotel?: PropertyFields.Fragment;
  selectedHotelId?: any;
  selectedMarker: any;
  zoom?: number;
  mapBounds?: any;
  preloadedHotelLoaded: boolean;
  showDismissButton: boolean;
  selectedPoiMarker?: any;
}
export type LocalizedSearchResultsMapProps = LocalizedComponentProps & SearchResultsMapProps;

const EGMaps = React.lazy(() => import('./eg-maps'));

@withRouter
@withLocalization
@inject('analytics', 'context', 'currentHotel', 'experiment', 'searchResults', 'searchCriteria', 'page')
@observer
export class SearchResultsMap extends React.Component<LocalizedSearchResultsMapProps, SearchResultsMapState> {
  private zIndex: number;
  private mapRef: React.RefObject<any>;
  private cancelHotelWatch: any;
  private trackMapPan: () => void;
  private trackMapZoomIn: () => void;
  private trackMapZoomOut: () => void;
  private trackMarkerSelect: (clientSideAnalytics) => void;
  private trackClusterSelect: () => void;
  private trackLandmarkSelect: () => void;
  private trackCardClosed: () => void;

  public constructor(props) {
    super(props);
    this.setUpAnalyticsTrackers(props.analytics);
    this.dismissSheet = this.dismissSheet.bind(this);
    this.onMapPan = this.onMapPan.bind(this);
    this.onZoomIn = this.onZoomIn.bind(this);
    this.onZoomOut = this.onZoomOut.bind(this);
    this.onMarkerSelect = this.onMarkerSelect.bind(this);
    this.onFeaturesPushed = this.onFeaturesPushed.bind(this);
    this.deSelectCurrentMapMarker = this.deSelectCurrentMapMarker.bind(this);
    this.mapRef = React.createRef<UitkEGMapsRef>();
    this.zIndex = props.searchResults.mapMarkers && props.searchResults.mapMarkers.length ? props.searchResults.properties.length : 0;
    this.state = {
      markerSelected: false,
      selectedHotel: undefined,
      selectedMarker: null,
      center: undefined,
      zoom: undefined,
      preloadedHotelLoaded: false,
      showDismissButton: false,
      selectedPoiMarker: undefined,
    };
  }

  private setUpAnalyticsTrackers(analytics) {
    const noop = () => null;
    const clickTracker = analytics && analytics.clickTracker ? analytics.clickTracker.bind(analytics) : noop;
    this.trackMapPan = clickTracker('Module.Map.Pan', 'User panning PWA HSR Map', null, noop);
    this.trackMapZoomIn = clickTracker('Module.Map.Zoom.In', 'User zooming in on PWA HSR Map', null, noop);
    this.trackMapZoomOut = clickTracker('Module.Map.Zoom.Out', 'User zooming out on PWA HSR Map', null, noop);
    this.trackCardClosed = clickTracker('Module.Map.HotelCard.Closed', 'Hotel card closed on HSR Map', null, noop);
    this.trackMarkerSelect = clientSideAnalytics => {
      const { refererId, linkname } = clientSideAnalytics;
      clickTracker(refererId, linkname, null, null)();
    };
    this.trackClusterSelect = clickTracker('Module.Map.HotelClusterSelect', 'User selecting a cluster on the PWA HSR map', null, noop);
    this.trackLandmarkSelect = clickTracker('Module.Map.LandmarkSelect', 'User selecting a Landmark on the PWA HSR map', null, noop);
  }

  public componentDidMount() {
    const { searchResults, analytics, location } = this.props;
    const locationState = location?.state || ({} as any);
    this.cancelHotelWatch = autorun(() => {
      if (locationState.selectedHotelId && locationState.selectedHotelId !== this.state.selectedHotel) {
        this.setState({
          selectedHotel: searchResults?.findProperty(locationState.selectedHotelId, locationState.selectedHotel?.travelAd),
          selectedMarker: this.findSelectedHotel(locationState.selectedHotelId),
        });
      }
      if (searchResults?.hasAtLeastOneProperty) {
        this.setState({
          selectedHotel: undefined,
          selectedMarker: null,
          markerSelected: false,
          preloadedHotelLoaded: false,
        });
      }
    });

    const mapFilters =
      searchResults && searchResults.map && searchResults.map.filterDisplayOptions && searchResults.map.filterDisplayOptions.filters;
    if (mapFilters && analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('.MapFilters.Imp', 'Showing map filters', null);
    }
  }

  public componentWillUnmount() {
    if (this.cancelHotelWatch) {
      this.cancelHotelWatch();
      this.cancelHotelWatch = undefined;
    }
  }

  public render() {
    const { context, searchResults, searchCriteria, experiment, dialogView, analytics } = this.props;
    const mapModel = searchResults && searchResults.map;
    const mapFilters = mapModel && mapModel.filterDisplayOptions && mapModel.filterDisplayOptions.filters;
    const toolBarAttributes = {
      type: ToolbarType.CLOSE,
      header: searchResults?.searchedDestination,
      subtext: mapModel && mapModel.subtitle,
      iconLabel: this.props.l10n.formatText('maps.closeButton.label'),
    };
    const toolbarAnalytics = analytics?.setupTracker('.Module.Map.Link', 'Close Map Dialog');
    const dialogProps = {
      dialogShow: true,
      strictFullscreen: true,
    };

    const mapProps = {
      ...mapModel,
      locale: context?.locale,
      onZoomIn: this.onZoomIn,
      onFeaturesPushed: this.onFeaturesPushed,
      onZoomOut: this.onZoomOut,
      onMarkerSelect: this.onMarkerSelect,
      onMapPan: this.onMapPan,
      onClusterSelect: this.trackClusterSelect,
      zoom: this.state.zoom || mapModel.zoom,
      mapBounds: this.state.mapBounds,
    };

    const showPropertySheet = this.state.selectedHotel && this.state.selectedMarker && this.state.selectedMarker.sheet;

    const mapSearchProps = {
      l10n: this.props.l10n,
      mapCenter: this.state.center,
      selectedHotel: this.state.selectedHotel,
      selectedMarker: this.state.selectedMarker,
      mapModel,
      showPropertySheet,
      searchResults,
      mapProps,
      mapMarkers: searchResults?.mapMarkers,
      selectedPoiMarker: this.state.selectedPoiMarker,
      regionId: searchCriteria?.regionId,
      mapRef: this.mapRef,
      preloadedHotelLoaded: this.state.showDismissButton,
      deSelectCurrentMapMarker: this.deSelectCurrentMapMarker,
    };

    const isUSFExperiment = experiment?.exposures.Universal_Sort_and_Filter_Lodging?.bucket === 1;

    return (
      <UitkFullscreenDialog {...dialogProps}>
        <UitkToolbar
          {...toolBarAttributes}
          action={this.props.onDismiss}
          analytics={toolbarAnalytics}
          actionContent={
            mapFilters && (
              <FiltersToggle
                buttonComponent={UitkTertiaryButton}
                isActive={true}
                isFullWidth={false}
                label="hotels.searchResults.openMapFiltersDialog.label"
                a11yMessage="hotels.searchResults.appliedFiltersCount.a11yLabel"
                rfrr=":MapFilters"
                stid="open-map-filters"
                filters={mapFilters}
                dialogParam="map-filter"
                dialogTitle={this.props.l10n.formatText('hotels.searchResults.openMapFiltersDialog.label')}
                showSeparator={false}
                dialogView={dialogView}
                isUSFexperiment={isUSFExperiment}
              />
            )
          }
        />

        <React.Suspense fallback={<div />}>
          <EGMaps {...mapSearchProps} />
        </React.Suspense>
      </UitkFullscreenDialog>
    );
  }

  public onMarkerSelect = (marker: FeatureObject) => {
    if (marker?.type === 'property') {
      this.setState({
        selectedHotelId: marker.id,
        showDismissButton: false,
      });
      const selectedMarker = this.props.searchResults?.mapMarkers.find(x => x?.sheet?.id === marker.id || x?.hotel?.hotelId === marker.id);
      this.trackMarkerSelect(selectedMarker?.clientSideAnalytics);
      this.selectMarker(selectedMarker);
    } else {
      this.setState({
        selectedHotelId: undefined,
        selectedMarker: undefined,
        markerSelected: false,
        selectedPoiMarker: marker,
      });
      this.trackLandmarkSelect();
    }
  };

  private onViewportChange = (center: LatLngArray, bounds: Bounds, zoom: number) => {
    this.props.searchResults?.displaySearchButton();
    this.setState({
      center,
      zoom,
      mapBounds: bounds,
    });
  };

  public onMapPan = (center: LatLngArray, bounds: Bounds, zoom: number) => {
    this.trackMapPan();
    this.onViewportChange(center, bounds, zoom);
  };

  public onZoomIn(center: LatLngArray, bounds: Bounds, zoom: number) {
    this.trackMapZoomIn();
    this.onViewportChange(center, bounds, zoom);
  }

  public onZoomOut(center: LatLngArray, bounds: Bounds, zoom: number) {
    this.trackMapZoomOut();
    this.onViewportChange(center, bounds, zoom);
  }

  public onFeaturesPushed(mapObj: EGMap) {
    const mapInfo = { mapObj: mapObj, mapRef: this.mapRef, preloadedHotelLoaded: this.state.preloadedHotelLoaded };
    const travelAdHotelMarker = getPreloadedTravelAdHotelMarker(mapInfo, this.props);
    const selectedMarker = getPinnedHotelMarker(mapInfo, this.props);
    if (travelAdHotelMarker) {
      this.setState({ preloadedHotelLoaded: true, showDismissButton: true });
      const travelAdHotelId = travelAdHotelMarker.sheet?.id || travelAdHotelMarker.hotel?.hotelId;
      this.mapRef.current.map().selectFeature(travelAdHotelId, 'property');
      this.selectMarker(travelAdHotelMarker);
    } else if (selectedMarker) {
      this.setState({
        preloadedHotelLoaded: true,
        showDismissButton: false,
      });
      const selectedHotelId = selectedMarker.sheet?.id || selectedMarker.hotel?.hotelId;
      this.mapRef.current.map().centerAndZoomMap([selectedMarker.position.lat, selectedMarker.position.lng]);
      this.mapRef.current.map().selectFeature(selectedHotelId, 'property');
      this.selectMarker(selectedMarker);
    }
  }

  public deSelectCurrentMapMarker(): void {
    this.trackCardClosed();
    this.mapRef.current.map().clearSelectedFeature();
    this.setState({
      markerSelected: false,
      selectedMarker: null,
      selectedHotel: undefined,
    });
  }

  private selectMarker(marker) {
    if (this.state.selectedMarker && typeof this.state.selectedMarker.blur === 'function') {
      this.state.selectedMarker.blur();
    }
    this.zIndex += 1;
    if (marker && typeof marker.focus === 'function') {
      marker.focus(this.zIndex);
    }
    this.setState({
      markerSelected: true,
      selectedMarker: marker,
      selectedHotel: findSelectedHotelGivenMarker(marker, this.props.searchResults),
    });
  }

  private findSelectedHotel(hotelId) {
    if (!hotelId) {
      return undefined;
    }
    const hotels = this.props.searchResults?.mapMarkers;
    const hotelContainer = hotels && hotels.find(h => h.id && h.id === hotelId);

    return (hotelContainer && hotelContainer.hotel) || null;
  }

  private dismissSheet() {
    const { selectedMarker } = this.state;
    if (selectedMarker && typeof selectedMarker.blur === 'function') {
      selectedMarker.blur();
    }
    this.setState({
      markerSelected: false,
    });
  }
}

export const getPinnedHotelMarker = (mapInfo: MapInfo, props: LocalizedSearchResultsMapProps): any => {
  const { location, searchResults } = props;
  const locationState = location?.state || ({} as any);
  let selectedMarker: any;
  if (
    searchResults?.properties[0]?.pinnedDetails &&
    mapInfo.mapObj &&
    mapInfo.mapRef.current &&
    !mapInfo.preloadedHotelLoaded &&
    !locationState.selectedHotelId
  ) {
    const hotelId = Number(searchResults?.properties[0]?.id);
    selectedMarker = searchResults?.mapMarkers.find(mapMarker => mapMarker?.sheet?.id === hotelId || mapMarker?.hotel?.hotelId === hotelId);
  }

  return selectedMarker;
};

export const getPreloadedTravelAdHotelMarker = (mapInfo: MapInfo, props: LocalizedSearchResultsMapProps): any => {
  const { searchResults, context, page, experiment, location, preloadedTravelAdEnabled } = props;
  const locationState = location?.state || ({} as any);
  const selectedTravelAdHotel = searchResults?.findFirstSponsoredProperty();
  if (
    preloadedTravelAdEnabled &&
    mapInfo.mapObj &&
    mapInfo.mapRef.current &&
    !mapInfo.preloadedHotelLoaded &&
    !locationState.selectedHotelId &&
    selectedTravelAdHotel
  ) {
    const travelAdHotelId = Number(selectedTravelAdHotel.id);
    const travelAdHotelMarker = searchResults?.mapMarkers.find(
      mapMarker => mapMarker?.sheet?.id === travelAdHotelId || mapMarker?.hotel?.hotelId === travelAdHotelId
    );
    if (
      travelAdHotelMarker &&
      context &&
      experiment &&
      page &&
      evaluateAndLogExperiment('Preloaded_Travel_Ad_on_SRP_Map', context, experiment, page) === 1
    ) {
      return travelAdHotelMarker;
    }
  }

  return undefined;
};

export const findSelectedHotelGivenMarker = (marker, searchResults?: SearchResultsStore) => {
  if (marker && marker.sheet) {
    const foundHotel = marker.sheet.id && searchResults?.findProperty(marker.sheet.id.toString(), marker.sheet.travelAd);

    if (foundHotel) {
      return foundHotel;
    }

    return marker.sheet;
  }

  return marker.hotel;
};
